import type {CanonicalProduct} from '../menu/product.ts';
import {modifierPriceKey, type BusinessPolicySnapshot} from '../policy/business-policy.ts';
import type {StaffPermission} from '../staff/staff-authority.ts';
import type {StoreSettingsPayload} from '../store-settings/store-settings.ts';
import {createSmtDeviceGateBootstrap, type SmtDeviceGateBootstrapSeed} from './smt-runtime-device-gate.ts';
import type {SmtRuntimeAdapterInputs, SmtRuntimeBootstrapResult} from './smt-runtime-bootstrap-contract.ts';

export interface AcceptanceRuntimeEnvironment {
  readonly deviceId: string;
  readonly now: () => string;
  readonly nextId: SmtRuntimeAdapterInputs['nextId'];
}

const STORE_ID = 'morefun-owner-acceptance';
const ROOT_ID = 'acceptance-root';
const OPERATOR_ID = 'acceptance-operator';

const permissions: readonly StaffPermission[] = [
  'manager.override','staff.admin','store.settings.draft.write','store.settings.validate','store.settings.publish',
  'store.settings.activate','store.settings.rollback','catalog.write','catalog.publish','availability.change',
  'availability.schedule','table.open','table.transfer','table.merge','table.split','table.clear',
  'order.sensitive-correction','payment.refund','payment.correction','business-day.open','service.manage',
  'shift.open','cash.movement','shift.close','report.view.history','report.export',
];

const products: readonly CanonicalProduct[] = [
  {id:'riceballA',status:'active',names:{customer:'A層 古早味飯團',operations:'A層 古早味飯團'},pricing:{selectorKeys:['combo-main']},optionGroups:[]},
  {id:'riceballC',status:'active',names:{customer:'C層 F4 飯團',operations:'C層 F4 飯團'},pricing:{selectorKeys:['combo-main']},optionGroups:[]},
  {id:'snack0',status:'active',names:{customer:'涼拌青瓜',operations:'涼拌青瓜'},pricing:{selectorKeys:['combo-snack']},optionGroups:[]},
  {id:'snack3',status:'active',names:{customer:'台灣一口腸 +$3',operations:'台灣一口腸 +$3'},pricing:{selectorKeys:['combo-snack']},optionGroups:[]},
  {id:'drinkHot',status:'active',names:{customer:'熱台式奶茶',operations:'熱台式奶茶'},pricing:{selectorKeys:['combo-drink']},optionGroups:[]},
  {id:'drinkIced',status:'active',names:{customer:'凍飲 +$3',operations:'凍飲 +$3'},pricing:{selectorKeys:['combo-drink']},optionGroups:[]},
  {id:'lemonTea',status:'active',names:{customer:'手打檸檬茶 +$10',operations:'手打檸檬茶 +$10'},pricing:{selectorKeys:['combo-drink']},optionGroups:[]},
  {id:'outsideDrink',status:'active',names:{customer:'非套餐測試飲品',operations:'非套餐測試飲品'},pricing:{selectorKeys:['drink-only']},optionGroups:[]},
  {
    id:'bento12',status:'active',names:{customer:'12 肉燥便當',operations:'12 肉燥便當'},pricing:{selectorKeys:['bento']},
    optionGroups:[
      {id:'rice-base',name:'飯底',enabled:true,required:true,min:1,max:1,order:1,options:[
        {id:'rice-normal',name:'正常飯',enabled:true,priceDeltaMinor:0,order:1},
        {id:'rice-half',name:'半飯',enabled:true,priceDeltaMinor:0,order:2},
        {id:'rice-less',name:'少飯',enabled:true,priceDeltaMinor:0,order:3},
      ]},
      {id:'extras',name:'加配',enabled:true,required:false,min:0,max:2,order:2,options:[
        {id:'extra-egg',name:'加蛋 +$6',enabled:true,priceDeltaMinor:600,order:1},
        {id:'extra-meat',name:'加肉 +$10',enabled:true,priceDeltaMinor:1000,order:2},
      ]},
    ],
  },
];

const prices: Readonly<Record<string, number>> = {
  riceballA:4100,riceballC:4500,snack0:1800,snack3:2100,drinkHot:1200,drinkIced:1500,lemonTea:2200,outsideDrink:1300,bento12:4800,
};

const visible = Object.fromEntries(products.map((p)=>[p.id,true]));
const nodes = Object.fromEntries(products.map((p)=>[`product:${p.id}`,'available' as const]));
const requirements = Object.fromEntries(products.map((p)=>[p.id,[`product:${p.id}`]]));

const policyFor = (businessDate:string): BusinessPolicySnapshot => ({
  policyVersion:`acceptance-policy:${businessDate}`,
  menuRevision:'acceptance-menu:v1',pricingRevision:'acceptance-pricing:v1',availabilityRevision:`acceptance-availability:${businessDate}`,rulesRevision:'acceptance-rules:v1',currency:'HKD',
  pricing:{
    productBaseMinor:prices,
    modifierOptionMinor:{
      [modifierPriceKey('bento12','rice-base','rice-normal')]:0,
      [modifierPriceKey('bento12','rice-base','rice-half')]:0,
      [modifierPriceKey('bento12','rice-base','rice-less')]:0,
      [modifierPriceKey('bento12','extras','extra-egg')]:600,
      [modifierPriceKey('bento12','extras','extra-meat')]:1000,
    },
  },
  visibility:{customer:visible,operations:visible},
  availability:{nodes,productRequirements:requirements},
  comboRules:[{
    ruleId:'riceball-set',enabled:true,priority:100,operationsLabel:'飯團套餐',customerLabel:'飯團套餐',
    requirements:[{selectorKey:'combo-main',quantity:1},{selectorKey:'combo-snack',quantity:1},{selectorKey:'combo-drink',quantity:1}],
    fixedBaseTotalMinor:0,
    componentProductMinor:{riceballA:4100,riceballC:4500,snack0:0,snack3:300,drinkHot:0,drinkIced:300,lemonTea:1000},
  }],
  modifierRules:[],
});

const settings: StoreSettingsPayload = {
  storeId:STORE_ID,storeName:'磨飯 Owner Acceptance',timezone:'Asia/Hong_Kong',currency:'HKD',locale:'zh-HK',
  businessHours:[
    {day:'mon',closed:false,opensAt:'11:00',closesAt:'20:00'},{day:'tue',closed:false,opensAt:'11:00',closesAt:'20:00'},
    {day:'wed',closed:false,opensAt:'11:00',closesAt:'20:00'},{day:'thu',closed:false,opensAt:'11:00',closesAt:'20:00'},
    {day:'fri',closed:false,opensAt:'11:00',closesAt:'20:00'},{day:'sat',closed:true},{day:'sun',closed:false,opensAt:'11:00',closesAt:'20:00'},
  ],
  serviceModes:['dine-in','takeaway'],dineInEnabled:true,takeawayEnabled:true,
  paymentMethodRefs:['cash','fps'],printRoutingRefs:['acceptance-receipt'],channelRefs:['store','customer'],
};

const businessDate = (timestamp:string):string => {
  const parts=new Intl.DateTimeFormat('en-CA',{timeZone:'Asia/Hong_Kong',year:'numeric',month:'2-digit',day:'2-digit'}).formatToParts(new Date(timestamp));
  const get=(type:Intl.DateTimeFormatPartTypes)=>parts.find((p)=>p.type===type)?.value ?? '';
  return `${get('year')}-${get('month')}-${get('day')}`;
};

export function createAcceptanceRuntimeBootstrap(environment:AcceptanceRuntimeEnvironment):SmtRuntimeBootstrapResult {
  const date=businessDate(environment.now());
  const seed:SmtDeviceGateBootstrapSeed={
    storeSettings:settings,storeReady:true,businessDate:date,basePolicy:policyFor(date),products,
    categories:[
      {categoryId:'riceball',label:'飯團',productIds:['riceballA','riceballC']},
      {categoryId:'snack',label:'小食',productIds:['snack0','snack3']},
      {categoryId:'drink',label:'飲品',productIds:['drinkHot','drinkIced','lemonTea','outsideDrink']},
      {categoryId:'bento',label:'便當',productIds:['bento12']},
    ],
    paymentMethods:[{methodId:'cash',label:'現金',kind:'cash'},{methodId:'fps',label:'FPS',kind:'recorded',methodCode:'fps',referenceAllowed:true}],
    rootActorId:ROOT_ID,operator:{staffId:OPERATOR_ID,displayName:'Acceptance Operator',permissions},
    tables:[
      {tableId:'T1',label:'1號枱',areaId:'main',capacity:2,position:1,enabled:true,mergeWith:['T2']},
      {tableId:'T2',label:'2號枱',areaId:'main',capacity:4,position:2,enabled:true,mergeWith:['T1']},
    ],
    queueClasses:[{classId:'small',prefix:'A',minPartySize:1,maxPartySize:2},{classId:'large',prefix:'B',minPartySize:3,maxPartySize:8}],
    resolveFulfillmentPolicy:()=>({requiresPaymentBeforeProduction:false,requiredTasks:[]}),
    deviceId:environment.deviceId,initialServiceMode:'takeaway',now:environment.now,nextId:environment.nextId,
  };
  return createSmtDeviceGateBootstrap(seed);
}
